import { fetchMaintenanceInfoApi } from "@/api/services/global/maintenance";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { MaintenanceCard } from "@/features/maintenance/components/MaintenanceCard";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { MAINTENANCE_MODE } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { Box, Stack, Typography } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";

// Full-screen maintenance page. Self-polls the public maintenance endpoint every 30s
// and auto-exits to home once maintenance is no longer active/unusable (normal mode,
// before window start, after window end, or readable mode).
export const Maintenance: FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isFirstLoad = useRef<boolean>(true);

  const getMaintenanceInfo = async () => {
    try {
      if (isFirstLoad.current) setIsLoading(true);
      const rs = await fetchMaintenanceInfoApi();
      const { data } = rs;
      if (data?.maintenanceMode && data?.maintenanceMode !== MAINTENANCE_MODE.SYSTEM_NORMAL_OPERATION) {
        // Before window start, after window end, or readable mode -> not full-screen maintenance; exit to home.
        if (data?.modeStartDateTime && dayjs().tz("Asia/Tokyo").isBefore(dayjs(data.modeStartDateTime).tz("Asia/Tokyo"))) {
          window.location.replace(AppRoutes.home);
        } else if (data?.modeEndDateTime && dayjs().tz("Asia/Tokyo").isAfter(dayjs(data.modeEndDateTime).tz("Asia/Tokyo"))) {
          window.location.replace(AppRoutes.home);
        } else if (data?.maintenanceMode === MAINTENANCE_MODE.MAINTENANCE_READABLE) {
          window.location.replace(AppRoutes.home);
        }
      } else {
        window.location.replace(AppRoutes.home);
      }
      return data;
    } catch (error) {
      showError(error);
    } finally {
      if (isFirstLoad.current) {
        setIsLoading(false);
        isFirstLoad.current = false;
      }
    }
  };

  useEffect(() => {
    getMaintenanceInfo();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      getMaintenanceInfo();
    }, 30000); // 30s to recheck
    return () => {
      clearInterval(timer);
    };
  }, []);

  if (isLoading) return <LoadingScreen />;
  return (
    <Box width="100%" display="flex" justifyContent="center" mt={13}>
      <Stack gap={7} alignItems="center">
        <Typography fontSize={50} fontWeight={600}>
          irohana
        </Typography>
        <Stack maxWidth={400} gap={6} alignItems="center">
          <Stack>
            <Typography variant="body14" color={text.error}>
              大変申し訳ございませんが、現在メンテナンス中です。
            </Typography>
            <Typography variant="body14" color={text.error}>
              お手数おかけしますが、時間を置いて再度アクセスください。
            </Typography>
          </Stack>
          <MaintenanceCard />
        </Stack>
      </Stack>
    </Box>
  );
};
